// ###### IMPORTS
import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { createAssetCategory, updateAssetCategory } from '../../viewServices/assets_api';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

const formatApiError = (data) => {
  if (!data) return 'Failed to save.';
  if (typeof data === 'string') return data;
  if (data.detail) {
    const d = data.detail;
    return Array.isArray(d) ? d.join(' ') : String(d);
  }
  const parts = [];
  Object.keys(data).forEach((k) => {
    const v = data[k];
    if (Array.isArray(v)) parts.push(...v.map((x) => String(x)));
    else if (v != null) parts.push(String(v));
  });
  return parts.length ? parts.join(' ') : 'Failed to save.';
};

const emptyForm = () => ({
  category_code: '',
  order: '0',
  level_0: '',
  level_1: '',
  level_2: '',
  level_3: '',
});

// ###### COMPONENT
/** `category` null/undefined = create mode; object with id = edit mode. */
const ModalEditAssetCategory = ({ isOpen, category, onClose, onSaved }) => {
  const isCreate = !category?.id;
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const applyCategory = useCallback(() => {
    if (!category?.id) {
      setForm(emptyForm());
      return;
    }
    setForm({
      category_code: category.category_code ?? '',
      order: String(category.order ?? 0),
      level_0: category.level_0 ?? '',
      level_1: category.level_1 ?? '',
      level_2: category.level_2 ?? '',
      level_3: category.level_3 ?? '',
    });
  }, [category]);

  useEffect(() => {
    if (!isOpen) return;
    applyCategory();
  }, [isOpen, category, applyCategory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = form.category_code?.trim();
    if (!code) {
      toast.error('Category code is required.');
      return;
    }
    const orderNum = parseInt(String(form.order).trim(), 10);
    const order = Number.isNaN(orderNum) ? 0 : orderNum;
    const payload = {
      category_code: code.slice(0, 30),
      order,
      level_0: (form.level_0 ?? '').trim().slice(0, 50),
      level_1: (form.level_1 ?? '').trim().slice(0, 50),
      level_2: (form.level_2 ?? '').trim().slice(0, 50),
      level_3: (form.level_3 ?? '').trim().slice(0, 50),
    };
    try {
      setSubmitting(true);
      if (isCreate) {
        await createAssetCategory(payload);
        toast.success('Category added.');
      } else {
        await updateAssetCategory(category.id, payload);
        toast.success('Category updated.');
      }
      await Promise.resolve(onSaved?.());
      onClose?.();
    } catch (err) {
      const fallback = isCreate ? 'Failed to add category.' : 'Failed to update category.';
      toast.error(formatApiError(err.response?.data) || err.message || fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onClose?.();
  };

  if (!isOpen) return null;

  const fieldClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-600 focus:border-green-600';

  return (
    <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex justify-center items-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg relative max-h-[90vh] flex flex-col">
        {submitting && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}

        <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 pr-2">
            {isCreate ? 'Add category' : 'Edit category'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="text-gray-500 hover:text-gray-700 shrink-0 p-1 rounded-lg hover:bg-gray-100"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-4 py-3 space-y-3 text-sm">
            <p className="text-xs text-gray-500">
              The Category Catalog suggests picker options and controls view aggregation. It stores the
              category code as a string in new records, with no link to historical data - only affecting
              the aggregated views.
              {!isCreate && (
                <>
                  {' '}
                  Editing or removing this entry does not change existing assets.
                </>
              )}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-1">
                <label htmlFor="cat-form-code" className="block text-xs font-medium text-gray-600 mb-1">
                  Category code
                </label>
                <input
                  id="cat-form-code"
                  name="category_code"
                  value={form.category_code}
                  onChange={handleChange}
                  maxLength={30}
                  disabled={submitting}
                  className={fieldClass}
                  required
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="cat-form-order" className="block text-xs font-medium text-gray-600 mb-1">
                  Order
                </label>
                <input
                  id="cat-form-order"
                  name="order"
                  type="number"
                  value={form.order}
                  onChange={handleChange}
                  disabled={submitting}
                  className={fieldClass}
                />
              </div>
            </div>

            {['level_0', 'level_1', 'level_2', 'level_3'].map((key) => (
              <div key={key}>
                <label htmlFor={`cat-form-${key}`} className="block text-xs font-medium text-gray-600 mb-1">
                  {key.replace('level_', 'Level ')}
                </label>
                <input
                  id={`cat-form-${key}`}
                  name={key}
                  value={form[key]}
                  onChange={handleChange}
                  maxLength={50}
                  disabled={submitting}
                  className={fieldClass}
                />
              </div>
            ))}
          </div>

          <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2 shrink-0 bg-gray-50 rounded-b-2xl">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-xl bg-green-700 hover:bg-green-800 text-white text-sm font-medium disabled:opacity-50"
            >
              {isCreate ? 'Add' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEditAssetCategory;
